import React from 'react';
import {
  Box, Typography, Stack, Button, Tooltip, Chip, Paper,
} from '@mui/material';
import {
  CheckCircle as DoneIcon,
  ArrowForward as ArrowIcon,
  PictureAsPdf as PdfIcon,
  Add as AddIcon,
  AttachFile as AttachIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import StatusBadge from './StatusBadge';

const TYPE_CONFIG = {
  quotation:     { label: 'ใบเสนอราคา',         color: '#F7931E' },
  delivery_note: { label: 'ใบส่งงาน',            color: '#7C3AED' },
  billing_note:  { label: 'ใบวางบิล/ใบแจ้งหนี้', color: '#1D4ED8' },
  receipt:       { label: 'ใบเสร็จรับเงิน',      color: '#059669' },
  tax_invoice:   { label: 'ใบกำกับภาษี',         color: '#0EA5E9' },
};

function groupChain(chain) {
  const quotation = chain.find((d) => d.document_type === 'quotation');
  const delivery = chain.find((d) => d.document_type === 'delivery_note');
  const billings = chain
    .filter((d) => d.document_type === 'billing_note')
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const receipts = chain.filter((d) => d.document_type === 'receipt');
  const taxInvoices = chain.filter((d) => d.document_type === 'tax_invoice');

  const installments = billings.map((billing, i) => {
    const billingId = billing._id?.toString();
    const receipt = receipts.find((r) => {
      const pid = r.parent_document_id?._id?.toString() || r.parent_document_id?.toString();
      return pid === billingId;
    }) || null;
    const receiptId = receipt?._id?.toString();
    const taxInvoice = taxInvoices.find((ti) => {
      const pid = ti.parent_document_id?._id?.toString() || ti.parent_document_id?.toString();
      // ตรงจาก receipt (ระบบใหม่) หรือตรงจาก billing_note (ข้อมูลเก่า)
      return pid === (receiptId || billingId);
    }) || null;
    return { billing, receipt, tax_invoice: taxInvoice, idx: i + 1 };
  });

  return { quotation, delivery, installments };
}

function DocCard({ doc, currentId, onPrint, type }) {
  const history = useHistory();
  const cfg = TYPE_CONFIG[type];
  const isCurrent = doc && (doc._id === currentId || doc.id === currentId);

  return (
    <Paper
      elevation={isCurrent ? 3 : 0}
      sx={{
        border: '1.5px solid',
        borderColor: cfg.color,
        borderRadius: 2.5,
        p: 1.5,
        minWidth: 148,
        maxWidth: 190,
        bgcolor: isCurrent ? `${cfg.color}12` : '#fff',
        flex: '0 0 auto',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
        <DoneIcon sx={{ fontSize: 13, color: cfg.color }} />
        <Typography variant="caption" fontWeight={700} sx={{ color: cfg.color, fontSize: 10 }}>
          {cfg.label}
        </Typography>
      </Stack>

      <Button
        variant="text" size="small"
        onClick={() => history.push(`/sale/documents/${doc._id}`)}
        sx={{
          p: 0, fontWeight: 700, fontSize: 11, color: cfg.color,
          textTransform: 'none', minWidth: 0, justifyContent: 'flex-start',
          mb: 0.5, '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' },
        }}
      >
        {doc.document_number}
      </Button>

      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" sx={{ mb: 0.75 }}>
        <StatusBadge status={doc.status} size="small" />
        {doc.revision > 1 && (
          <Chip
            label={`Rev ${String(doc.revision).padStart(2, '0')}`}
            size="small"
            sx={{ height: 16, fontSize: 10, bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600 }}
          />
        )}
      </Stack>

      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {onPrint && (
          <Tooltip title="พิมพ์ PDF">
            <Button
              size="small" variant="outlined"
              startIcon={<PdfIcon sx={{ fontSize: 12 }} />}
              onClick={() => onPrint(doc)}
              sx={{
                borderRadius: 1.5, fontSize: 10, py: 0.25, px: 0.75,
                borderColor: cfg.color, color: cfg.color,
                '&:hover': { bgcolor: `${cfg.color}10` },
              }}
            >
              PDF
            </Button>
          </Tooltip>
        )}
        {type === 'delivery_note' && doc.po_document_url && (
          <Tooltip title="ดูใบ PO จากลูกค้า">
            <Button
              size="small" variant="outlined"
              startIcon={<AttachIcon sx={{ fontSize: 12 }} />}
              onClick={() => window.open(doc.po_document_url, '_blank')}
              sx={{
                borderRadius: 1.5, fontSize: 10, py: 0.25, px: 0.75,
                borderColor: '#15803d', color: '#15803d',
              }}
            >
              PO
            </Button>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
}

function Arrow({ active }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', pt: 2.5, flexShrink: 0 }}>
      <ArrowIcon sx={{ fontSize: 16, color: active ? '#94a3b8' : '#d1d5db' }} />
    </Box>
  );
}

export default function ChainTimeline({ chain = [], currentId, onCreateNext, onPrint }) {
  const { quotation, delivery, installments } = groupChain(chain);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, p: 2.5, bgcolor: '#fafafa' }}>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2 }}>
        Document Chain
      </Typography>

      {/* บน: ใบเสนอราคา → ใบส่งงาน */}
      <Stack direction="row" alignItems="flex-start" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        {quotation && (
          <>
            <DocCard doc={quotation} currentId={currentId} onPrint={onPrint} type="quotation" />
            <Arrow active={!!delivery} />
          </>
        )}
        {delivery ? (
          <DocCard doc={delivery} currentId={currentId} onPrint={onPrint} type="delivery_note" />
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11, alignSelf: 'center' }}>
            ยังไม่มีใบส่งงาน
          </Typography>
        )}
      </Stack>

      {/* ล่าง: งวดการชำระเงิน */}
      {(installments.length > 0 || delivery) && (
        <Box sx={{ borderLeft: '2px solid #e2e8f0', ml: 1, pl: 2 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: 11, display: 'block', mb: 1.5 }}>
            งวดการชำระเงิน
          </Typography>

          <Stack spacing={2}>
            {installments.map(({ billing, receipt, tax_invoice, idx }) => (
              <Box key={billing._id}>
                <Typography variant="caption" sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, mb: 0.75, display: 'block' }}>
                  งวดที่ {idx}
                </Typography>
                <Stack direction="row" alignItems="flex-start" flexWrap="wrap" gap={1}>
                  <DocCard doc={billing} currentId={currentId} onPrint={onPrint} type="billing_note" />
                  <Arrow active={!!receipt} />

                  {receipt ? (
                    <>
                      <DocCard doc={receipt} currentId={currentId} onPrint={onPrint} type="receipt" />
                      <Arrow active={!!tax_invoice} />
                      {tax_invoice ? (
                        <DocCard doc={tax_invoice} currentId={currentId} onPrint={onPrint} type="tax_invoice" />
                      ) : (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11, alignSelf: 'center' }}>
                          ยังไม่มีใบกำกับภาษี
                        </Typography>
                      )}
                    </>
                  ) : (
                    onCreateNext && (
                      <Box sx={{ alignSelf: 'center' }}>
                        <Button
                          size="small" variant="outlined"
                          startIcon={<ReceiptIcon sx={{ fontSize: 13 }} />}
                          onClick={() => onCreateNext('receipt_invoice', billing)}
                          sx={{
                            borderRadius: 1.5, fontSize: 11, py: 0.25, px: 1,
                            borderColor: TYPE_CONFIG.receipt.color, color: TYPE_CONFIG.receipt.color,
                            '&:hover': { bgcolor: `${TYPE_CONFIG.receipt.color}10` },
                          }}
                        >
                          + ใบเสร็จ/กำกับภาษี
                        </Button>
                      </Box>
                    )
                  )}
                </Stack>
              </Box>
            ))}

            {/* ปุ่มเพิ่มงวด */}
            {delivery && onCreateNext && (
              <Box>
                <Button
                  size="small" variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                  onClick={() => onCreateNext('billing_note', delivery)}
                  sx={{
                    borderRadius: 1.5, fontSize: 11, py: 0.25, px: 1,
                    borderColor: TYPE_CONFIG.billing_note.color, color: TYPE_CONFIG.billing_note.color,
                    '&:hover': { bgcolor: `${TYPE_CONFIG.billing_note.color}10` },
                  }}
                >
                  + เพิ่มงวด
                </Button>
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
